import type {
  Block,
  HeadingBlock,
  ImageBlock,
  ImageFrame,
  ImagePlacement,
  MarginNoteBlock,
  PinSide,
  SceneBlock,
  ThoughtBlock,
  TextBlock,
  ThoughtLikeBlock,
  UnfoldBlock,
  VoiceBlock,
} from '../../lib/types'
import {
  anchorIndex,
  ensureTrailingText,
  insertAfter,
  isBlockEmpty,
  isEditable,
  isOutOfFlow,
  isParagraph,
  isPinnedNote,
  newBlockId,
  pinnedRanges,
  unfoldAnchorsIn,
  unwrapUnfold,
} from '../../lib/blocks'
import { clampWidth, isWrap, widthRange } from '../../lib/imageSize'
import { collapseEmptiedCells, columnGroups, columnsBlocks, groupAt, groupOf, isColumnsMarker, MAX_COLUMNS, normalizeColumns, setColumnWidths } from '../../lib/columns'
import { escapeHtml } from '../../lib/text'
import type { PastedPart } from '../../lib/paste'

// Composer v4 document operations. Margin notes, voice memos, pins and
// unfolds are stored right after the column block they belong to, so a
// column block and the out-of-flow blocks after it move and insert as a
// group; everything here keeps those groups together.

/** Index of the last block in the group that starts at `index` (the block plus the out-of-flow blocks stored after it). */
export function groupEnd(blocks: Block[], index: number): number {
  let j = index
  while (j + 1 < blocks.length && isOutOfFlow(blocks[j + 1])) j++
  return j
}

/**
 * Where a column block inserted "after `afterId`" lands. An empty paragraph
 * is replaced (insertAfter's rule), so it stays the target; any other block
 * keeps its margin notes, pins and unfolds, so the new block goes after them.
 */
export function insertionPoint(blocks: Block[], afterId: string | null): string | null {
  if (!afterId) return null
  const i = blocks.findIndex((b) => b.id === afterId)
  if (i < 0) return null
  const b = blocks[i]
  if (b.type === 'text' && isBlockEmpty(b)) return afterId
  return blocks[groupEnd(blocks, i)].id
}

/** Insert column blocks after `afterId` (or at the end), keeping margin groups intact. */
export function insertColumn(blocks: Block[], afterId: string | null, inserted: Block[]): Block[] {
  return insertAfter(blocks, insertionPoint(blocks, afterId), inserted)
}

/**
 * Insert a new Columns group after `afterId`, never inside another one: from
 * inside a cell it goes after that group. Returns where the caret goes (the
 * first cell's paragraph).
 */
export function insertColumns(blocks: Block[], afterId: string | null, count: number, newId: () => string): { blocks: Block[]; focusId: string } {
  const g = groupOf(blocks, afterId)
  const inserted = columnsBlocks(count, newId)
  return { blocks: insertColumn(blocks, g ? blocks[g.end].id : afterId, inserted), focusId: inserted[1].id }
}

/**
 * Insert an out-of-flow block (margin note, voice memo, pin, unfold) for
 * `afterId`. From an empty line it lands just above that line, so it
 * belongs to the block before it and the line stays for typing; otherwise it
 * joins the end of `afterId`'s group. With no target it goes at the end.
 */
export function insertAside(blocks: Block[], afterId: string | null, block: Block): Block[] {
  const out = blocks.slice()
  const i = afterId ? out.findIndex((b) => b.id === afterId) : -1
  if (i < 0) {
    let j = out.length
    while (j > 0 && out[j - 1].type === 'text' && isBlockEmpty(out[j - 1])) j--
    out.splice(j, 0, block)
    return ensureTrailingText(out)
  }
  const target = out[i]
  if (target.type === 'text' && isBlockEmpty(target)) out.splice(i, 0, block)
  else out.splice(groupEnd(out, i) + 1, 0, block)
  return out
}

/** The column block a margin item hangs from: the nearest one before it (null when there's none). */
export function asideAnchor(blocks: Block[], id: string): string | null {
  const i = blocks.findIndex((b) => b.id === id)
  for (let j = i - 1; j >= 0; j--) if (!isOutOfFlow(blocks[j])) return blocks[j].id
  return null
}

/** The column block a new margin item inserted after `afterId` would hang from. */
export function insertedAsideAnchor(blocks: Block[], afterId: string | null): string | null {
  const probe: Block = { id: '\u0000probe', type: 'margin', text: '' }
  return asideAnchor(insertAside(blocks, afterId, probe), probe.id)
}

/** A thought-like block's snapshot as a column thought, keeping its id. */
export function asColumnThought(b: ThoughtLikeBlock): ThoughtBlock {
  const out: ThoughtBlock = { id: b.id, type: 'thought', text: b.text }
  if (b.source) out.source = b.source
  if (b.own) out.own = true
  if (b.thoughtId) out.thoughtId = b.thoughtId
  if (b.capturedAt) out.capturedAt = b.capturedAt
  if (b.kind) out.kind = b.kind
  return out
}

function holderOf(blocks: Block[], anchor: string): Block | undefined {
  return blocks.find((x) => isEditable(x) && unfoldAnchorsIn(x.html).includes(anchor))
}

/**
 * Move a margin note, pin or unfold into the column as a sourced thought,
 * after its anchor's group. An unfold's words are unwrapped first. Returns
 * the id of the editable block whose html changed (it must remount), if any.
 */
export function intoColumn(blocks: Block[], id: string): { blocks: Block[]; touched: string | null } {
  const i = blocks.findIndex((b) => b.id === id)
  const b = blocks[i]
  if (!b || !(b.type === 'margin' || b.type === 'unfold')) return { blocks, touched: null }
  let anchorId: string | null = (() => {
    const a = anchorIndex(blocks, i)
    return a === null ? null : blocks[a].id
  })()
  let out = blocks.filter((x) => x.id !== id)
  let touched: string | null = null
  if (b.type === 'unfold') {
    const holder = holderOf(out, b.anchor)
    if (holder && isEditable(holder)) {
      touched = holder.id
      anchorId = holder.id
      out = out.map((x) => (x.id === holder.id && isEditable(x) ? { ...x, html: unwrapUnfold(x.html, b.anchor) } : x))
    }
  }
  const thought = asColumnThought(b)
  const ai = anchorId ? out.findIndex((x) => x.id === anchorId) : -1
  out.splice(ai < 0 ? Math.min(i, out.length) : groupEnd(out, ai) + 1, 0, thought)
  return { blocks: out, touched }
}

/** Remove an unfold and unwrap its words. Returns the editable whose html changed, if any. */
export function removeUnfold(blocks: Block[], id: string): { blocks: Block[]; touched: string | null } {
  const u = blocks.find((b): b is UnfoldBlock => b.id === id && b.type === 'unfold')
  if (!u) return { blocks, touched: null }
  const holder = holderOf(blocks, u.anchor)
  const out = blocks
    .filter((b) => b.id !== id)
    .map((b) => (holder && b.id === holder.id && isEditable(b) ? { ...b, html: unwrapUnfold(b.html, u.anchor) } : b))
  return { blocks: out, touched: holder?.id ?? null }
}

/**
 * Keep unfolds attached to their words, run on save. An unfold whose words
 * were edited away becomes a column thought (nothing the author wrote is
 * lost); one whose words moved to another block (a split, a merge) moves
 * under that block. Returns the same array when nothing changed.
 */
export function normalizeUnfolds(blocks: Block[]): { blocks: Block[]; detached: number } {
  let out = blocks
  let detached = 0
  for (const u of blocks) {
    if (u.type !== 'unfold') continue
    const holder = holderOf(out, u.anchor)
    if (!holder) {
      out = intoColumn(out, u.id).blocks
      detached++
      continue
    }
    const i = out.findIndex((x) => x.id === u.id)
    const a = anchorIndex(out, i)
    if (a !== null && out[a].id === holder.id) continue
    const rest = out.filter((x) => x.id !== u.id)
    const hi = rest.findIndex((x) => x.id === holder.id)
    rest.splice(groupEnd(rest, hi) + 1, 0, u)
    out = rest
  }
  return { blocks: out, detached }
}

/** The most paragraphs a margin note can be pinned across (lib/blocks.ts). */
export { maxPinSpan } from '../../lib/blocks'

/**
 * Move a block to after `afterId`'s group (drag to reorder). A column block
 * brings its margin items and unfolds along; an out-of-flow block moves on
 * its own, to sit beside its new neighbour. A column it leaves empty folds
 * away (collapseEmptiedCells).
 */
export function moveGroup(blocks: Block[], id: string, afterId: string | null): Block[] {
  const i = blocks.findIndex((b) => b.id === id)
  if (i < 0 || id === afterId) return blocks
  const b = blocks[i]
  // Columns move whole, by their start; a break or end never moves alone.
  if (isColumnsMarker(b) && b.part !== 'start') return blocks
  const group = isColumnsMarker(b) ? groupAt(blocks, i) : null
  if (isColumnsMarker(b) && !group) return blocks
  const end = group ? group.end : isOutOfFlow(b) ? i : groupEnd(blocks, i)
  const moving = blocks.slice(i, end + 1)
  if (afterId && moving.some((x) => x.id === afterId)) return blocks
  const rest = [...blocks.slice(0, i), ...blocks.slice(end + 1)]
  let target = afterId
  if (group) {
    // Dropped into other columns: after them instead (no nesting).
    const into = groupOf(rest, afterId)
    if (into) target = rest[into.end].id
  }
  const t = target ? rest.findIndex((x) => x.id === target) : -1
  // After a cell's marker is the top of that cell (a marker's group is itself).
  const at = t < 0 ? rest.length : groupEnd(rest, t) + 1
  rest.splice(at, 0, ...moving)
  return collapseEmptiedCells(blocks, normalizeColumns(ensureTrailingText(rest)))
}

/**
 * Where the image hand (components/editor/useImageHand.ts) puts an image:
 * just before the column block `beforeId` (null = the end), at `placement`,
 * `offset` lines down that block when it's a wrap. The image brings its
 * margin items along. Same array when nothing changes, or when the image is
 * inside columns (the hand doesn't move those).
 */
export function placeImage(blocks: Block[], id: string, beforeId: string | null, placement: ImagePlacement, offset = 0): Block[] {
  const i = blocks.findIndex((b) => b.id === id)
  const img = blocks[i]
  if (!img || img.type !== 'image' || id === beforeId || groupOf(blocks, id)) return blocks
  const end = groupEnd(blocks, i)
  const off = isWrap(placement) ? Math.max(0, Math.round(offset)) : 0
  const next: ImageBlock = { ...img, placement }
  if (off) next.offset = off
  else delete next.offset
  const moving = [next, ...blocks.slice(i + 1, end + 1)]
  const rest = [...blocks.slice(0, i), ...blocks.slice(end + 1)]
  const t = beforeId === null ? rest.length : rest.findIndex((b) => b.id === beforeId)
  if (t < 0 || moving.some((x) => x.id === beforeId)) return blocks
  const sameSpot = t === i
  if (sameSpot && (img.placement ?? 'inline') === placement && (img.offset ?? 0) === off) return blocks
  rest.splice(t, 0, ...moving)
  return sameSpot ? rest : normalizeColumns(ensureTrailingText(rest))
}

/**
 * The block to drop or insert after so the result lands just before `id`
 * (a drop on a block's top half, as in Composer v3): the column block (or
 * columns marker) before it, skipping margin items. Null when there's none,
 * i.e. `id` is the first thing in the document.
 */
export function afterForBefore(blocks: Block[], id: string): string | null {
  const i = blocks.findIndex((b) => b.id === id)
  for (let j = i - 1; j >= 0; j--) if (!isOutOfFlow(blocks[j])) return blocks[j].id
  return null
}

export type Side = 'left' | 'right'

/**
 * Can something be dropped beside `targetId` as a new column (the gutter
 * drop)? Beside any block in the column that isn't a scene, an image or a
 * marker; inside columns, only while they have room for another cell. Not
 * beside an image, which would put the image in a column (Niki, 10/5: images
 * wrap, never columns); an image dropped beside a block wraps it instead
 * (wrapBeside).
 */
export function canPlaceBeside(blocks: Block[], targetId: string): boolean {
  const i = blocks.findIndex((b) => b.id === targetId)
  const b = blocks[i]
  if (!b || isOutOfFlow(b) || isColumnsMarker(b) || b.type === 'scene' || b.type === 'image') return false
  const g = groupAt(blocks, i)
  return !g || g.cells.length < MAX_COLUMNS
}

/**
 * Put `inserted` beside `targetId` as a column of its own (the gutter drop).
 * Outside columns the target (with its margin items) and the new blocks
 * become a two-column group; inside columns the new blocks become a new cell
 * on that side of the target's cell. Returns the blocks unchanged when there
 * is no room.
 */
export function placeBeside(blocks: Block[], targetId: string, side: Side, inserted: Block[], newId: () => string = newBlockId): Block[] {
  if (!inserted.length || !canPlaceBeside(blocks, targetId)) return blocks
  const i = blocks.findIndex((b) => b.id === targetId)
  const g = groupAt(blocks, i)
  const brk = (): Block => ({ id: newId(), type: 'columns', part: 'break' })
  const out = blocks.slice()
  if (g) {
    const cell = g.cells.find((c) => i >= c.from && i < c.to)!
    if (side === 'right') out.splice(cell.to, 0, brk(), ...inserted)
    else out.splice(cell.from, 0, ...inserted, brk())
    // A new cell: the columns start out even again.
    return normalizeColumns(ensureTrailingText(setColumnWidths(out, g.startId, null)))
  }
  const end = groupEnd(blocks, i)
  const target = blocks.slice(i, end + 1)
  const cells = side === 'right' ? [target, inserted] : [inserted, target]
  out.splice(
    i,
    end + 1 - i,
    { id: newId(), type: 'columns', part: 'start' },
    ...cells[0],
    brk(),
    ...cells[1],
    { id: newId(), type: 'columns', part: 'end' },
  )
  return normalizeColumns(ensureTrailingText(out))
}

/** Move a block (with its margin items) beside `targetId` as a new column. Unchanged when it can't go there. */
export function moveBeside(blocks: Block[], id: string, targetId: string, side: Side, newId: () => string = newBlockId): Block[] {
  const i = blocks.findIndex((b) => b.id === id)
  const b = blocks[i]
  if (!b || id === targetId || isOutOfFlow(b) || isColumnsMarker(b)) return blocks
  const end = groupEnd(blocks, i)
  const moving = blocks.slice(i, end + 1)
  if (moving.some((x) => x.id === targetId)) return blocks
  // The column it leaves folds away first if that empties it, so there's room beside the target.
  const rest = collapseEmptiedCells(blocks, normalizeColumns(ensureTrailingText([...blocks.slice(0, i), ...blocks.slice(end + 1)]), newId))
  if (!rest.some((x) => x.id === targetId)) return blocks
  const out = placeBeside(rest, targetId, side, moving, newId)
  return out === rest ? blocks : out
}

// ── Images wrap, never columns (Niki, 10/5) ──────────────────────────────

/**
 * Can an image dropped at `targetId`'s edge wrap it? Beside a block in the
 * page's own flow (not inside Columns, which an image no longer joins) that
 * text can run beside: not a scene, another image or a columns marker.
 */
export function canWrapBeside(blocks: Block[], targetId: string): boolean {
  const i = blocks.findIndex((b) => b.id === targetId)
  const b = blocks[i]
  if (!b || isOutOfFlow(b) || isColumnsMarker(b) || b.type === 'scene' || b.type === 'image') return false
  return !groupAt(blocks, i)
}

/** An image as a wrap on `side`: level with the block's first line, its width kept only if a wrap can have it. */
function asWrap(img: ImageBlock, side: Side): ImageBlock {
  const next: ImageBlock = { ...img, placement: side === 'left' ? 'wrap-left' : 'wrap-right' }
  delete next.offset
  if (next.width !== undefined && clampWidth(next.placement, next.width) !== next.width) delete next.width
  return next
}

/**
 * The ⋮ drop at a block's edge for an image (and a photo from the thoughts
 * panel): the image goes just before `targetId` as a wrap on that side, so
 * the block's text runs beside it. It brings its margin items along, and
 * leaves Columns if it was in a cell (an emptied cell folds away). Unchanged
 * when it can't wrap there.
 */
export function wrapBeside(blocks: Block[], id: string, targetId: string, side: Side): Block[] {
  const i = blocks.findIndex((b) => b.id === id)
  const img = blocks[i]
  if (!img || img.type !== 'image' || id === targetId || !canWrapBeside(blocks, targetId)) return blocks
  const end = groupEnd(blocks, i)
  const moving = [asWrap(img, side), ...blocks.slice(i + 1, end + 1)]
  if (moving.some((x) => x.id === targetId)) return blocks
  const rest = [...blocks.slice(0, i), ...blocks.slice(end + 1)]
  const t = rest.findIndex((b) => b.id === targetId)
  if (t < 0) return blocks
  rest.splice(t, 0, ...moving)
  return collapseEmptiedCells(blocks, normalizeColumns(ensureTrailingText(rest)))
}

/** A new image (a photo from the thoughts panel) wrapped beside `targetId`. Unchanged when it can't wrap there. */
export function insertWrapped(blocks: Block[], targetId: string, side: Side, img: ImageBlock): Block[] {
  if (!canWrapBeside(blocks, targetId)) return blocks
  const t = blocks.findIndex((b) => b.id === targetId)
  const out = blocks.slice()
  out.splice(t, 0, asWrap(img, side))
  return normalizeColumns(ensureTrailingText(out))
}

/**
 * Where an image dropped at `afterId` goes: never into Columns, so a drop
 * anywhere in a Columns group lands after the whole group (its end marker).
 */
export function imageDropTarget(blocks: Block[], afterId: string | null): string | null {
  const g = groupOf(blocks, afterId)
  return g ? blocks[g.end].id : afterId
}

// ── Layout: column rows, margin items and pinned ranges ──────────────────

/** A column block with what renders around it. */
export interface Row {
  /** The column block; null for margin items stored before the first one. */
  block: Block | null
  /** Right-margin items (margin notes, voice memos), stacked in order. */
  asides: Array<MarginNoteBlock | VoiceBlock>
  /** Unfold cards, shown under the block. */
  unfolds: UnfoldBlock[]
  /** Pinned notes stored here that have nothing to stay beside; shown inline. */
  loosePins: MarginNoteBlock[]
}

/** Outside columns: a row, or a pin with the rows it stays beside. */
export type FlowSegment = { kind: 'row'; row: Row } | { kind: 'pin'; pin: MarginNoteBlock; rows: Row[] }

/** Columns: each cell laid out on its own, as a little document. */
export interface ColumnsSegment {
  kind: 'columns'
  startId: string
  /** The end marker: what an image dropped on the group goes after (images don't join columns). */
  endId: string
  cells: Array<{ markerId: string; segments: FlowSegment[] }>
}

export type Segment = FlowSegment | ColumnsSegment

/**
 * Group the document into column rows, wrap each pin's range so its card
 * can stick beside it, and gather each Columns group into its cells.
 */
export function layoutBlocks(blocks: Block[]): Segment[] {
  const groups = columnGroups(blocks)
  if (!groups.length) return layoutFlow(blocks)
  const out: Segment[] = []
  let from = 0
  for (const g of groups) {
    out.push(...layoutFlow(blocks.slice(from, g.start)))
    out.push({
      kind: 'columns',
      startId: g.startId,
      endId: blocks[g.end].id,
      cells: g.cells.map((c) => ({ markerId: c.markerId, segments: layoutFlow(blocks.slice(c.from, c.to)) })),
    })
    from = g.end + 1
  }
  out.push(...layoutFlow(blocks.slice(from)))
  return out
}

/** layoutBlocks for a run with no columns in it. Stray markers are skipped. */
function layoutFlow(all: Block[]): FlowSegment[] {
  const blocks = all.some(isColumnsMarker) ? all.filter((b) => !isColumnsMarker(b)) : all
  const ranges = pinnedRanges(blocks)
  const pins = new Map<string, MarginNoteBlock>()
  for (const b of blocks) if (isPinnedNote(b)) pins.set(b.id, b)
  const pinStartingAt = new Map<string, MarginNoteBlock>()
  for (const [pinId, ids] of ranges) if (ids.length) pinStartingAt.set(ids[0], pins.get(pinId)!)

  const rows: Row[] = []
  let cur: Row | null = null
  for (const b of blocks) {
    if (!isOutOfFlow(b)) {
      cur = { block: b, asides: [], unfolds: [], loosePins: [] }
      rows.push(cur)
      continue
    }
    if (!cur) {
      cur = { block: null, asides: [], unfolds: [], loosePins: [] }
      rows.push(cur)
    }
    if (isPinnedNote(b)) {
      if (!ranges.get(b.id)?.length) cur.loosePins.push(b)
    } else if (b.type === 'margin' || b.type === 'voice') cur.asides.push(b)
    else if (b.type === 'unfold') cur.unfolds.push(b)
  }

  const out: FlowSegment[] = []
  let open: { pin: MarginNoteBlock; ids: Set<string>; rows: Row[] } | null = null
  for (const row of rows) {
    const id = row.block?.id
    if (open && id && open.ids.has(id)) {
      open.rows.push(row)
      continue
    }
    if (open) {
      out.push({ kind: 'pin', pin: open.pin, rows: open.rows })
      open = null
    }
    const pin = id ? pinStartingAt.get(id) : undefined
    if (pin) open = { pin, ids: new Set(ranges.get(pin.id)), rows: [row] }
    else out.push({ kind: 'row', row })
  }
  if (open) out.push({ kind: 'pin', pin: open.pin, rows: open.rows })
  return out
}

/**
 * Pin a margin note across `span` paragraphs (its own first), or unpin it
 * (null): an ordinary note again, back in the right margin.
 */
export function setPinSpan(blocks: Block[], id: string, span: number | null): Block[] {
  return blocks.map((b) => {
    if (b.id !== id || b.type !== 'margin') return b
    if (span === null) {
      // Unpinned, it's an ordinary note in the right margin: its pin's side goes too.
      const { span: _old, side: _side, ...rest } = b
      return rest
    }
    const { span: _old, ...rest } = b
    return { ...rest, span: Math.max(1, Math.floor(span)) }
  })
}

/**
 * Which margin a pinned note sits in (Niki, 10/2). The left is the default,
 * so it's stored only as `side: 'right'`.
 */
export function setPinSide(blocks: Block[], id: string, side: PinSide): Block[] {
  return blocks.map((b) => {
    if (b.id !== id || b.type !== 'margin') return b
    const { side: _old, ...rest } = b
    return side === 'right' ? { ...rest, side } : rest
  })
}

/**
 * An image or scene with a new crop & position, or none (null: the default
 * framing, so the key goes rather than sitting there undefined).
 */
export function withFrame<B extends ImageBlock | SceneBlock>(block: B, frame: ImageFrame | null): B {
  const next = { ...block }
  delete next.frame
  return frame ? { ...next, frame } : next
}

/**
 * An image block with another photo in it (Replace, or a file dropped on it).
 * It stays the same block where it sits: id, caption, placement, a wrap's
 * offset and its width, so the new photo takes the old one's place at the
 * old one's size. The crop goes: a frame is measured against its photo's own
 * aspect (`natural`), so on a different photo its zoom, focus and free-crop
 * shape would land somewhere arbitrary, or squash it. With no frame the new
 * photo just covers its box (full width and an uncropped inline take its own
 * shape), and Reposition is there to frame it again. It's no longer the
 * thought's photo either, so the thought link and stamp go with the crop.
 */
export function withNewPhoto(block: ImageBlock, src: string): ImageBlock {
  const { frame: _frame, thoughtId: _thought, capturedAt: _at, kind: _kind, ...rest } = block
  return { ...rest, src }
}

/**
 * An image at a new width (percent of the column, clamped to its placement).
 * Null, or the placement's default, drops the key: an absent width is the
 * default, so the image renders as it did before it was resized.
 */
export function withWidth(block: ImageBlock, width: number | null): ImageBlock {
  const next = { ...block }
  delete next.width
  if (width === null) return next
  const w = clampWidth(block.placement, width)
  return w === widthRange(block.placement).def ? next : { ...next, width: w }
}

/**
 * Delete a selection that runs across column blocks, from inside `startId` to
 * inside `endId` (in document order). `head` is what's left of the first block
 * before the selection and `tail` what's left of the last one after it, each
 * as inline HTML, or null when that end is inside a card (it goes with the
 * selection). Every column block between goes too; margin notes, pins and
 * unfolds stay and gather under the block that's left. The head and tail
 * join into one block, as deleting across paragraphs does in any editor.
 */
export function deleteAcross(
  blocks: Block[],
  startId: string,
  endId: string,
  head: string | null,
  tail: string | null,
  newId: () => string,
): { blocks: Block[]; keptId: string } {
  const a = blocks.findIndex((b) => b.id === startId)
  const z = blocks.findIndex((b) => b.id === endId)
  if (a < 0 || z < a) return { blocks, keptId: startId }
  const first = blocks[a]
  const last = blocks[z]
  if (blocks.slice(a + 1, z).some(isColumnsMarker)) {
    // Across a cell's edge: the two ends stay apart (in their own cells), the
    // markers and margin items between stay, and the column blocks between go.
    const head1 = head !== null && isEditable(first) ? ({ ...first, html: head } as Block) : null
    const tail1 = tail !== null && isEditable(last) ? ({ ...last, html: tail } as Block) : null
    const middle = blocks.slice(a + 1, z).filter((b) => isOutOfFlow(b) || isColumnsMarker(b))
    const out = normalizeColumns(
      ensureTrailingText([...blocks.slice(0, a), ...(head1 ? [head1] : []), ...middle, ...(tail1 ? [tail1] : []), ...blocks.slice(z + 1)]),
      newId,
    )
    const keptId = head1?.id ?? tail1?.id ?? out.slice(a).find(isEditable)?.id ?? startId
    return { blocks: out, keptId }
  }
  let kept: Block
  if (head !== null && isEditable(first)) kept = { ...first, html: head + (tail ?? '') }
  else if (tail !== null && isEditable(last)) kept = { ...last, html: tail }
  else kept = { id: newId(), type: 'text', html: '' }
  const between = blocks.slice(a + 1, z + 1).filter(isOutOfFlow)
  return { blocks: ensureTrailingText([...blocks.slice(0, a), kept, ...between, ...blocks.slice(z + 1)]), keptId: kept.id }
}

const blankHtml = (html: string) => !html.replace(/<br\s*\/?>/g, '').trim()

/**
 * Paste over a selection from inside `startId` to inside `endId` (the same
 * block for a selection in one paragraph): `head`, `tail` and `null` mean what
 * they do for deleteAcross. The selection goes; a first pasted paragraph joins
 * the head, a heading pasted over a whole empty line turns the line into it,
 * the rest follow as their own blocks (after the kept block's margin notes),
 * and the tail comes after them. Where the caret goes: `at` characters into
 * block `id`, counted from the end of the head when `afterHead`.
 */
export function pasteAcross(
  blocks: Block[],
  startId: string,
  endId: string,
  head: string | null,
  tail: string | null,
  parts: PastedPart[],
  newId: () => string,
): { blocks: Block[]; keptId: string; focus: { id: string; at: number; afterHead: boolean } } {
  const a = blocks.findIndex((b) => b.id === startId)
  const z = blocks.findIndex((b) => b.id === endId)
  /** The block that stays (deleteAcross's rule): the first one, else the last, else a new paragraph. */
  const keeps = head !== null ? blocks[a] : tail !== null ? blocks[z] : undefined
  const first = parts[0]
  const intoLine = (keeps?.type ?? 'text') === 'text' && blankHtml(head ?? '') && blankHtml(tail ?? '')
  const headingHere = first?.kind === 'heading' && intoLine ? first : null
  const lead = first?.kind === 'text' ? first.text : ''
  const rest = headingHere || lead ? parts.slice(1) : parts
  const extra: Array<TextBlock | HeadingBlock> = rest.map((p) =>
    p.kind === 'heading' ? { id: newId(), type: 'heading', html: escapeHtml(p.text), level: p.level ?? 2 } : { id: newId(), type: 'text', html: escapeHtml(p.text) },
  )
  // Across a column cell's edge the tail stays in its own cell; anywhere else it moves on past the pasted blocks.
  const apart = head !== null && a >= 0 && blocks.slice(a + 1, z).some(isColumnsMarker)
  const carry = extra.length > 0 && tail !== null && !apart
  const ins = escapeHtml(lead)
  const res = deleteAcross(
    blocks,
    startId,
    endId,
    head === null ? null : head + ins,
    tail === null ? null : (head === null ? ins : '') + (carry ? '' : tail),
    newId,
  )
  if (res.blocks === blocks && a !== z) return { blocks, keptId: startId, focus: { id: startId, at: 0, afterHead: true } }
  const out = res.blocks.slice()
  const k = out.findIndex((b) => b.id === res.keptId)
  const kept = out[k]
  if (headingHere) {
    out[k] = { id: kept.id, type: 'heading', html: escapeHtml(headingHere.text), level: headingHere.level ?? 2 }
  } else if (head === null && tail === null && ins && isEditable(kept)) {
    out[k] = { ...kept, html: ins } as Block
  }
  if (carry && !blankHtml(tail!)) {
    const last = extra[extra.length - 1]
    if (last.type === 'text') last.html += tail
    else extra.push({ id: newId(), type: 'text', html: tail! })
  }
  out.splice(groupEnd(out, k) + 1, 0, ...extra)
  const lastPasted = extra[rest.length - 1]
  const focus = lastPasted
    ? { id: lastPasted.id, at: rest[rest.length - 1].text.length, afterHead: false }
    : headingHere
      ? { id: kept.id, at: headingHere.text.length, afterHead: false }
      : { id: kept.id, at: lead.length, afterHead: head !== null }
  return { blocks: out, keptId: res.keptId, focus }
}
